
import { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export interface UrlRule {
  url: string;
  condition: "always" | "contains" | "not_contains";
  keywords?: string[];
}

interface AddUrlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddUrl: (urlRule: UrlRule) => void;
  initialUrl?: string;
  isEditing?: boolean;
  ruleType?: "webkit" | "socket";
}

// Helper function to parse existing URL rules
export const parseUrlRule = (url: string): UrlRule => {
  // Check if URL starts with ! (negation)
  if (url.startsWith('!')) {
    const parts = url.substring(1).trim().split(' ');
    const baseUrl = parts[0];
    const keywords = parts.slice(1).filter(k => k.trim());
    
    return {
      url: baseUrl,
      condition: "contains",
      keywords: keywords.length > 0 ? keywords : undefined
    };
  }
  
  // Check if URL has keywords (space-separated)
  const parts = url.split(' ');
  if (parts.length > 1) {
    const baseUrl = parts[0];
    const keywords = parts.slice(1).filter(k => k.trim());
    
    return {
      url: baseUrl,
      condition: "not_contains",
      keywords: keywords.length > 0 ? keywords : undefined
    };
  }
  
  // Simple URL without conditions
  return {
    url: url,
    condition: "always"
  };
};

// URL validation function
const validateUrl = (url: string, isBlockAll: boolean): { isValid: boolean; error?: string } => {
  if (isBlockAll) {
    return { isValid: true }; // Block all is always valid
  }
  
  if (!url.trim()) {
    return { isValid: false, error: "URL is required" };
  }
  
  // Check for protocol prefixes (case insensitive)
  const urlLower = url.toLowerCase();
  if (urlLower.startsWith('http://') || urlLower.startsWith('https://')) {
    return { isValid: false, error: "Please remove 'http://' or 'https://' from the URL" };
  }
  
  return { isValid: true };
};

export const AddUrlModal = ({ isOpen, onClose, onAddUrl, initialUrl, isEditing = false, ruleType = "socket" }: AddUrlModalProps) => {
  const [url, setUrl] = useState("");
  const [condition, setCondition] = useState<"always" | "contains" | "not_contains">("always");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [urlValidation, setUrlValidation] = useState<{ isValid: boolean; error?: string }>({ isValid: true });
  const [isBlockAll, setIsBlockAll] = useState(false);

  // Effect to handle initial URL when editing
  useEffect(() => {
    if (isEditing && initialUrl) {
      // Check if it's a "block all" rule (just a star)
      if (initialUrl.trim() === "*") {
        setIsBlockAll(true);
        setUrl("");
        setCondition("always");
        setKeywords([]);
      } else {
        const parsed = parseUrlRule(initialUrl);
        setIsBlockAll(false);
        setUrl(parsed.url);
        setCondition(parsed.condition);
        setKeywords(parsed.keywords || []);
      }
      // Validate the initial URL
      setUrlValidation(validateUrl(initialUrl.trim() === "*" ? "" : initialUrl, initialUrl.trim() === "*"));
    } else if (!isEditing) {
      // Reset form when not editing
      setUrl("");
      setCondition("always");
      setKeywords([]);
      setKeywordInput("");
      setUrlValidation({ isValid: true });
      setIsBlockAll(false);
    }
  }, [isEditing, initialUrl, isOpen]);

  // Validate URL whenever it changes or block all changes
  useEffect(() => {
    setUrlValidation(validateUrl(url, isBlockAll));
  }, [url, isBlockAll]);

  // Handle block all checkbox change
  const handleBlockAllChange = (checked: boolean) => {
    setIsBlockAll(checked);
    if (checked) {
      setUrl("");
      setCondition("always");
      setKeywords([]);
      setKeywordInput("");
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput("");
    }
  };

  const handleKeywordKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleRemoveKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  // Convert user-friendly format to backend format
  const convertToBackendFormat = (baseUrl: string, condition: string, keywords: string[]): string => {
    if (condition === "always") {
      return baseUrl;
    }
    
    if (condition === "contains") {
      // "contains" means we want to block only when keywords are present
      // This translates to: !baseUrl keyword1 keyword2
      return `!${baseUrl} ${keywords.join(" ")}`;
    }
    
    if (condition === "not_contains") {
      // "not_contains" means we want to block except when keywords are present
      // This translates to: baseUrl keyword1 keyword2 (normal rule with exceptions)
      return `${baseUrl} ${keywords.join(" ")}`;
    }
    
    return baseUrl;
  };

  const handleAddUrl = () => {
    if (isBlockAll) {
      // For "block all", just send a star
      const urlRule: UrlRule = {
        url: "*",
        condition: "always"
      };
      onAddUrl(urlRule);
    } else {
      if (!url.trim() || !urlValidation.isValid) return;

      // Convert to backend format for storage
      const backendUrl = convertToBackendFormat(url.trim(), condition, keywords);

      // Create UrlRule object that preserves the user's intent for display
      const urlRule: UrlRule = {
        url: backendUrl, // Store the converted format
        condition,
        keywords: condition !== "always" ? keywords : undefined
      };

      onAddUrl(urlRule);
    }
    
    // Reset form only if not editing
    if (!isEditing) {
      setUrl("");
      setCondition("always");
      setKeywords([]);
      setKeywordInput("");
      setUrlValidation({ isValid: true });
      setIsBlockAll(false);
    }
    onClose();
  };

  const handleCancel = () => {
    // Reset form
    setUrl("");
    setCondition("always");
    setKeywords([]);
    setKeywordInput("");
    setUrlValidation({ isValid: true });
    setIsBlockAll(false);
    onClose();
  };

  const getConditionText = () => {
    switch (condition) {
      case "contains":
        return "only when it contains the following";
      case "not_contains":
        return "only when it does not contain the following";
      default:
        return "always";
    }
  };

  const getPreviewText = () => {
    const actionWord = "Block";
    const trafficWord = ruleType === "webkit" ? "allowed" : "blocked";
    
    if (isBlockAll) {
      return ruleType === "webkit" 
        ? "Block all network traffic for this bundle ID (WebKit default behavior)"
        : "Block all network traffic for this bundle ID";
    }
    
    const baseText = `${actionWord} "${url || "URL"}" ${getConditionText()}`;
    if (keywords.length > 0 && condition !== "always") {
      return `${baseText} (${keywords.join(", ")})`;
    }
    return baseText;
  };

  const showKeywordInput = !isBlockAll && (condition === "contains" || condition === "not_contains");
  const isFormValid = isBlockAll || (url.trim() && urlValidation.isValid && (condition === "always" || keywords.length > 0));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit URL Rule" : "Add URL Rule"}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">

          {!isBlockAll && (
            <>
              <div>
                <Label htmlFor="url" className="text-sm font-medium text-gray-700">
                  URL
                </Label>
                <Input
                  id="url"
                  placeholder="Enter URL (e.g., google.com)"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className={`mt-1 ${!urlValidation.isValid ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                />
                {!urlValidation.isValid && (
                  <p className="mt-1 text-sm text-red-600">{urlValidation.error}</p>
                )}
                <div className="mt-2 text-xs text-gray-500 bg-blue-50 p-2 rounded border-l-4 border-blue-200">
                  <strong>Important:</strong> Do not include "http://" or "https://" in the URL. Just enter the domain (e.g., google.com).
                </div>
                <div className="mt-2 text-xs text-gray-500 bg-blue-50 p-2 rounded border-l-4 border-blue-200">
                  <strong>Wildcard tip:</strong> Use * before the URL to block only subdomains (e.g., *.example.com blocks sub.example.com but not example.com). To block both the main domain and subdomains, add two separate URLs.
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  {ruleType === "webkit" ? "Block this URL when" : "Block this URL when"}
                </Label>
                <Select value={condition} onValueChange={(value: "always" | "contains" | "not_contains") => setCondition(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="always">always</SelectItem>
                    <SelectItem value="contains">only when it contains the following</SelectItem>
                    <SelectItem value="not_contains">only when it does not contain the following</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {showKeywordInput && (
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Keywords/Conditions
                  </Label>
                  <div className="space-y-2">
                    <div className="flex space-x-2">
                      <Input
                        placeholder="Type keyword and press Enter"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyPress={handleKeywordKeyPress}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleAddKeyword}
                        disabled={!keywordInput.trim()}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {keywords.map((keyword, index) => (
                          <Badge key={index} variant="secondary" className="flex items-center gap-1">
                            {keyword}
                            <button
                              onClick={() => handleRemoveKeyword(index)}
                              className="ml-1 hover:text-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            <strong>Preview:</strong> {getPreviewText()}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddUrl}
            disabled={!isFormValid}
          >
            {isEditing ? "Update URL Rule" : "Add URL Rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
